import type { Reducer } from 'react';
import type { AssetTypename, FieldValueValues } from '@frameio/graphql-client';
import type { AssetAggregateAppearance } from '@frameio/graphql-client/generated';
import type { ComposerMode } from '../../comments/types';

export type AssetClickBehavior = 'COMMENT' | 'PLAY_PAUSE';

/** root AppState object */
export type AppState = {
  assetClickBehavior: AssetClickBehavior;
  selectedAssetType: AssetTypename | null;
  commentScroll: {
    /** the id of the comment to scroll to */
    id: string;
    /** the comment scroll behavior */
    scrollType: CommentScrollType;
    /** was the comment created due to a subscription? */
    fromSubscription: boolean;
  } | null;
  hasCompletedMigrationFlowInThisSession: boolean;
  isMobileNavOpen: boolean;
  moveToCopyToStatus: MoveToCopyToStatus;
  pointAndClickStatus: {
    /** whether or not P&C pins are visible on the asset */
    arePinsVisible: boolean;
  };
  commentStatus: {
    isCreatedCommentHiddenByFilters: boolean;
  };
  viewAllAnnotationsStatus: {
    /** wheter or not view all annotations is in use */
    isViewAllAnnotationsEnabled: boolean;
  };
  viewSuggestionsStatus: {
    /** whether or not P&C pins are visible on the asset */
    areSuggestionsVisible: boolean;
  };
  suggestionModeStatus: {
    /** whether or not suggestion mode is available (unavailable if current page does not contain text) */
    isSuggestionModeAvailable: boolean;
    /** whether or not suggestion mode is enabled */
    isSuggestionModeEnabled: boolean;
    /** whether or not edit suggestion mode is enabled */
    isEditSuggestionModeEnabled: boolean;
  };
  /** whether attachments should hide to allow new page/frame to load first */
  areAnnotationsVisible: boolean;
  commentContainerTriggerType: CommentContainerTriggerType;
  /** will ignore the editor initialization for a related mode */
  ignoreEditorInitializationMode: ComposerMode | null;
  isInProjectSearchOpen: boolean;
  shareAppearance: Pick<AssetAggregateAppearance, 'layout'> | null;
  accessRevoked: boolean;
  currentlyEditingAssetField: {
    /** the value of the AssetFieldValue currently being edited */
    value: FieldValueValues;
    /** the ids of the AssetFieldValues currently being edited */
    fieldValueIds: string[] | null;
  };
  scrollToWorkspaceId: string | null;
  scrollToNewProjectWorkspaceId: string | null;
  panelVisibilityHistory: {
    wasCommentsPanelForcedClosed: boolean | null;
    wasTranscriptionPanelForcedClosed: boolean | null;
  };
  isFullscreenActive: boolean;
};

export const DEFAULT_STATE: AppState = {
  assetClickBehavior: 'PLAY_PAUSE',
  selectedAssetType: null,
  commentScroll: null,
  hasCompletedMigrationFlowInThisSession: false,
  isMobileNavOpen: false,
  moveToCopyToStatus: 'hidden',
  pointAndClickStatus: {
    arePinsVisible: true,
  },
  commentStatus: {
    isCreatedCommentHiddenByFilters: false,
  },
  viewAllAnnotationsStatus: {
    isViewAllAnnotationsEnabled: false,
  },
  viewSuggestionsStatus: {
    areSuggestionsVisible: true,
  },
  suggestionModeStatus: {
    isSuggestionModeEnabled: false,
    isEditSuggestionModeEnabled: false,
    isSuggestionModeAvailable: false,
  },
  areAnnotationsVisible: true,
  commentContainerTriggerType: null,
  ignoreEditorInitializationMode: null,
  isInProjectSearchOpen: false,
  shareAppearance: null,
  accessRevoked: false,
  currentlyEditingAssetField: {
    value: null,
    fieldValueIds: null,
  },
  scrollToWorkspaceId: null,
  scrollToNewProjectWorkspaceId: null,
  panelVisibilityHistory: {
    wasCommentsPanelForcedClosed: null,
    wasTranscriptionPanelForcedClosed: null,
  },
  isFullscreenActive: false,
};

export type AppStateActions =
  | {
      type: 'SET_ASSET_CLICK_BEHAVIOR';
      payload: {
        assetClickBehavior: AssetClickBehavior;
      };
    }
  | {
      type: 'ASSET_PANEL_LIST_COLUMN_ICONS';
      payload: boolean;
    }
  /** scroll to a particular comment id on the CommentList */
  | {
      type: 'SCROLL_TO_COMMENT';
      payload?: {
        id: string;
        scrollType?: CommentScrollType;
        fromSubscription?: boolean;
      };
    }
  /** scroll to a particular workspace on the workspaces page */
  | {
      type: 'SCROLL_TO_WORKSPACE';
      payload: {
        workspaceId: string;
      };
    }
  /** scroll to a particular workspace on the workspaces page */
  | {
      type: 'CLEAR_SCROLL_TO_WORKSPACE';
    }
  /** scroll to a particular workspace on the workspaces page */
  | {
      type: 'SCROLL_TO_NEW_PROJECT';
      payload: {
        workspaceId: string;
      };
    }
  | {
      type: 'CLEAR_SCROLL_TO_NEW_PROJECT';
    }
  | {
      type: 'MOVE_TO_COPY_TO_STATUS_CHANGE';
      payload: MoveToCopyToStatus;
    }
  /** set the visibility of P&C pins on or off */
  | {
      type: 'SET_PIN_VISIBILITY';
      payload: boolean;
    }
  /** set the type of comment who triggered an action */
  | {
      type: 'SET_COMMENT_TRIGGER';
      payload: CommentContainerTriggerType;
    }
  /** set the use of view all annotations on or off */
  | {
      type: 'SET_VIEW_ALL_ANNOTATIONS';
      payload: boolean;
    }
  /** set suggestions visibility to on or off */
  | {
      type: 'SET_SUGGESTIONS_VISIBILITY';
      payload: boolean;
    }
  /** set suggestion mode availability to on or off */
  | {
      type: 'SET_SUGGESTION_MODE_AVAILABLE';
      payload: boolean;
    }
  /** set suggestion mode to on or off */
  | {
      type: 'SET_SUGGESTION_MODE_ENABLED';
      payload: boolean;
    }
  /** set edit suggestion mode to on or off */
  | {
      type: 'SET_EDIT_SUGGESTION_MODE_ENABLED';
      payload: boolean;
    }
  /** set the visibility of a toast indicating that a comment was hidden by filters */
  | {
      type: 'SET_COMMENT_HIDDEN_BY_FILTERS_TOAST';
      payload: boolean;
    }
  /** set the visibility of attachments between loading the asset timestamp */
  | {
      type: 'SET_ANNOTATIONS_VISIBILITY';
      payload: boolean;
    }
  /** will ignore the editor initialization for a related mode */
  | {
      type: 'SET_IGNORE_EDITOR_INITIALIZATION_MODE';
      payload: ComposerMode | null;
    }
  | {
      type: 'SET_HAS_COMPLETED_MIGRATION_FLOW_IN_THIS_SESSION';
    }
  | {
      type: 'OPEN_MOBILE_NAV';
    }
  | {
      type: 'CLOSE_MOBILE_NAV';
    }
  | {
      type: 'SET_SHARE_APPEARANCE';
      payload: Pick<AssetAggregateAppearance, 'layout'>;
    }
  | {
      type: 'ACCESS_REVOKED';
      payload: boolean;
    }
  | {
      type: 'SET_ASSET_TYPE';
      payload: {
        selectedAssetType: AssetTypename;
      };
    }
  | {
      type: 'SET_CURRENTLY_EDITING_ASSET_FIELDS';
      payload: {
        value: FieldValueValues;
        fieldValueId: string[];
      };
    }
  | {
      type: 'TOGGLE_IS_IN_PROJECT_SEARCH_OPEN';
      payload: {
        isOpen: boolean;
      };
    }
  | {
      type: 'SET_PANEL_VISIBILITY_HISTORY';
      payload: {
        wasCommentsPanelForcedClosed: boolean;
        wasTranscriptionPanelForcedClosed: boolean;
      };
    }
  | {
      type: 'SET_FULLSCREEN_ACTIVE';
      payload: boolean;
    };

export const appStateReducer: Reducer<AppState, AppStateActions> = (
  state,
  action
): AppState => {
  if (action.type === 'SCROLL_TO_COMMENT') {
    return {
      ...state,
      commentScroll: action?.payload
        ? {
            id: action.payload.id,
            scrollType: action.payload?.scrollType ?? null,
            fromSubscription: action.payload?.fromSubscription ?? false,
          }
        : null,
    };
  }

  if (action.type === 'SET_ASSET_CLICK_BEHAVIOR') {
    return {
      ...state,
      assetClickBehavior: action?.payload.assetClickBehavior,
    };
  }

  if (action.type === 'SCROLL_TO_WORKSPACE') {
    return {
      ...state,
      scrollToWorkspaceId: action?.payload.workspaceId,
    };
  }
  if (action.type === 'CLEAR_SCROLL_TO_WORKSPACE') {
    return {
      ...state,
      scrollToWorkspaceId: null,
    };
  }

  if (action.type === 'SCROLL_TO_NEW_PROJECT') {
    return {
      ...state,
      scrollToNewProjectWorkspaceId: action?.payload.workspaceId,
    };
  }
  if (action.type === 'CLEAR_SCROLL_TO_NEW_PROJECT') {
    return {
      ...state,
      scrollToNewProjectWorkspaceId: null,
    };
  }

  if (action.type === 'MOVE_TO_COPY_TO_STATUS_CHANGE') {
    return {
      ...state,
      moveToCopyToStatus: action.payload,
    };
  }

  if (action.type === 'SET_PIN_VISIBILITY') {
    return {
      ...state,
      pointAndClickStatus: {
        ...state.pointAndClickStatus,
        arePinsVisible: action.payload,
      },
    };
  }

  if (action.type === 'SET_VIEW_ALL_ANNOTATIONS') {
    return {
      ...state,
      viewAllAnnotationsStatus: {
        ...state.viewAllAnnotationsStatus,
        isViewAllAnnotationsEnabled: action.payload,
      },
    };
  }

  if (action.type === 'SET_SUGGESTIONS_VISIBILITY') {
    return {
      ...state,
      viewSuggestionsStatus: {
        ...state.viewSuggestionsStatus,
        areSuggestionsVisible: action.payload,
      },
    };
  }

  if (action.type === 'SET_SUGGESTION_MODE_AVAILABLE') {
    return {
      ...state,
      suggestionModeStatus: {
        ...state.suggestionModeStatus,
        isSuggestionModeAvailable: action.payload,
      },
    };
  }

  if (action.type === 'SET_SUGGESTION_MODE_ENABLED') {
    return {
      ...state,
      suggestionModeStatus: {
        ...state.suggestionModeStatus,
        isSuggestionModeEnabled: action.payload,
      },
    };
  }

  if (action.type === 'SET_EDIT_SUGGESTION_MODE_ENABLED') {
    return {
      ...state,
      suggestionModeStatus: {
        ...state.suggestionModeStatus,
        isEditSuggestionModeEnabled: action.payload,
      },
    };
  }

  if (action.type === 'SET_ANNOTATIONS_VISIBILITY') {
    if (state.areAnnotationsVisible === action.payload) {
      return state;
    }

    return {
      ...state,
      areAnnotationsVisible: action.payload,
    };
  }

  if (action.type === 'SET_COMMENT_TRIGGER') {
    const nextTriggerType = action.payload || null;
    if (state.commentContainerTriggerType === nextTriggerType) {
      return state;
    }

    return {
      ...state,
      commentContainerTriggerType: nextTriggerType,
    };
  }

  if (action.type === 'SET_COMMENT_HIDDEN_BY_FILTERS_TOAST') {
    return {
      ...state,
      commentStatus: {
        ...state.commentStatus,
        isCreatedCommentHiddenByFilters: action.payload,
      },
    };
  }

  if (action.type === 'SET_IGNORE_EDITOR_INITIALIZATION_MODE') {
    return {
      ...state,
      ignoreEditorInitializationMode: action.payload || null,
    };
  }

  if (action.type === 'SET_HAS_COMPLETED_MIGRATION_FLOW_IN_THIS_SESSION') {
    return {
      ...state,
      hasCompletedMigrationFlowInThisSession: true,
    };
  }

  if (action.type === 'OPEN_MOBILE_NAV') {
    return {
      ...state,
      isMobileNavOpen: true,
    };
  }

  if (action.type === 'CLOSE_MOBILE_NAV') {
    return {
      ...state,
      isMobileNavOpen: false,
    };
  }

  if (action.type === 'SET_ASSET_TYPE') {
    return {
      ...state,
      selectedAssetType: action.payload.selectedAssetType,
    };
  }

  /**
   * The Shares review experience does not fire any mutations to update
   * appearance - in order to persist appearance across views/components, we'll
   * store select properties in this context.
   */
  if (action.type === 'SET_SHARE_APPEARANCE') {
    return {
      ...state,
      shareAppearance: action?.payload
        ? {
            layout: action.payload?.layout,
          }
        : null,
    };
  }

  if (action.type === 'ACCESS_REVOKED') {
    return {
      ...state,
      accessRevoked: action.payload,
    };
  }

  if (action.type === 'SET_CURRENTLY_EDITING_ASSET_FIELDS') {
    return {
      ...state,
      currentlyEditingAssetField: {
        value: action.payload.value,
        fieldValueIds: action.payload.fieldValueId,
      },
    };
  }

  if (action.type === 'TOGGLE_IS_IN_PROJECT_SEARCH_OPEN') {
    return {
      ...state,
      isInProjectSearchOpen: action.payload.isOpen,
    };
  }

  if (action.type === 'SET_PANEL_VISIBILITY_HISTORY') {
    return {
      ...state,
      panelVisibilityHistory: action.payload,
    };
  }

  if (action.type === 'SET_FULLSCREEN_ACTIVE') {
    return {
      ...state,
      isFullscreenActive: action.payload,
    };
  }

  return state;
};

export type CommentScrollType = 'new-comment' | 'new-reply' | null;
export type MoveToCopyToStatus = 'move' | 'copy' | 'hidden';
export type CommentContainerTriggerType =
  | 'point-and-click'
  | 'asset-details-panel'
  | 'comment-bar'
  | null;
